import useRetrieveData from 'global/hooks/useRetrieveData';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
// import { Button, IconButton } from '../../../../global/components/Button';
import SnackAlerts from '../../../../global/components/snack-alerts';
import SortableList from '../../../../global/components/sortable-list';
import { Text } from '../../../../global/components/text';
import { useWindowSize } from '../../../../global/hooks/useWindowSize';
import { first, get, orderBy, size } from '../../../../global/lib/lodash';
import { CenterLoader } from '../../../../global/styles';
import { convertToPostStructure } from '../../../../global/utils';
import sendData from '../../../../redux/actions/sendData';
import { RoomAssignmentsContainer } from './style';
import { RoomAssignmentChecklist } from './RoomAssignmentChecklist';
import { applyRoomLocationAccessFromSourceToTarget, isCheckpointTaggedForRoom } from '../assignment-list/AssignmentRows';
import { settingsCopiedSnackAlert } from '../assignment-list/assignment-copy-snacks';
import { AssignmentLocationRow } from '../assignment-list/AssignmentLocationRow';
import {
  ASSIGNMENT_FORM_TAB,
  AssignmentFormFooter,
  AssignmentFormTabPanel,
  AssignmentFormTabs,
  AssignmentsModalFrame,
  AssignmentPreviewDialogFrame,
} from '../assignment-list/assignment-form-shell';

const INCLUDES = [
  'realm',
  'checklists',
  'checklists.checkpoints',
  'checklists.tradeTypes',
];

const EMPTY_ROOM_GROUPS = () => [
  { key: '0', title: 'Enabled', items: [], canDrag: false },
  { key: '1', title: 'Disabled', items: [], canDrag: false },
];

/**
 * Merges scorecard checklists with realm room list using config.roomCheckpoints.
 * Uses room → checkpoint-id lists (usually far fewer rooms than checkpoints), then
 * one pass over checkpoints to attach rooms.
 */
function mergeScorecardRoomAssignments(scorecardData, roomList) {
  const roomCheckpoints = get(scorecardData, 'config.roomCheckpoints', []);
  const baseChecklists = get(scorecardData, 'checklists', []);

  const rcpByRoomId = new Map();
  roomCheckpoints.forEach((r, index) => {
    rcpByRoomId.set(String(r.id), { rcp: r, index });
  });

  const checklistsOut = baseChecklists.map((cl) => ({
    ...cl,
    checkpoints: cl.checkpoints.map((cp) => ({
      ...cp,
      rooms: [],
    })),
  }));

  const cpIdToRooms = new Map();
  const enabled = { key: '0', title: 'Enabled', items: [], canDrag: false };
  const disabled = { key: '1', title: 'Disabled', items: [], canDrag: false };

  for (const room of roomList || []) {
    const found = rcpByRoomId.get(String(room.id));
    const rcp = found ? found.rcp : null;
    const rcpi = found ? found.index : -1;

    if (!rcp) {
      disabled.items.push(room);
      continue;
    }

    if (size(rcp.cps)) {
      for (const cpId of rcp.cps) {
        const k = String(cpId);
        let list = cpIdToRooms.get(k);
        if (!list) {
          list = [];
          cpIdToRooms.set(k, list);
        }
        list.push(room);
      }
    }

    if (size(rcp.cps) || rcp.act) {
      enabled.items.push({ ...room, index: rcpi });
    } else {
      disabled.items.push(room);
    }
  }

  enabled.items = orderBy(enabled.items, 'index');

  for (const cl of checklistsOut) {
    for (const cp of cl.checkpoints) {
      const assigned = cpIdToRooms.get(String(cp.id));
      if (size(assigned)) {
        cp.rooms = assigned;
      }
    }
  }

  return { checklists: checklistsOut, rooms: [enabled, disabled] };
}

export const RoomAssignments = (props) => {
  const theme = useTheme();
  const { scorecard, onChange: notifyParentChange } = props;
  const [rooms, setRooms] = useState(EMPTY_ROOM_GROUPS);
  const [checklists, setChecklists] = useState([]);
  const [state, setState] = useState({
    copiedRooms: null,
    copiedLocationAccess: null,
    loading: false,
  });
  const [alerts, setAlerts] = useState();
  const [preview, setPreview] = useState({
    open: false,
    subTitle: '',
    onClose: () => {
      setPreview((p) => ({ ...p, open: false }));
    },
  });
  const [previewShowOnlyChecked, setPreviewShowOnlyChecked] = useState(false);
  const [mainTab, setMainTab] = useState(ASSIGNMENT_FORM_TAB.location);

  useEffect(() => {
    setState((s) => ({
      ...s,
      copiedRooms: null,
      copiedLocationAccess: null,
    }));
  }, [mainTab]);

  const { isMobileXS } = useWindowSize();

  const roomData = useRetrieveData(
    { url: '/realms' },
    {
      build: ['realms'],
      params: {
        filter: {
          tier: 'room',
        },
      },
    }
  );

  const scdata = useRetrieveData(
    { url: `/scorecards/${scorecard.id}` },
    {
      build: ['scorecards'],
      params: {
        include: INCLUDES.join(','),
      },
      transformData: (t) => {
        const card = first(t);
        const clists = get(card, 'checklists', []).map((cl) => {
          const { checkpoints } = cl;
          return {
            ...cl,
            checkpoints: orderBy(checkpoints, 'name'),
          };
        });
        return {
          ...card,
          checklists: clists,
        };
      },
    }
  );

  const supersets = useRetrieveData(
    { url: '/supersets' },
    {
      build: ['supersets'],
    }
  );

  const isLoading = supersets.loading || roomData.loading || scdata.loading;
  const criteriaLoading = scdata.loading || roomData.loading;

  const renderRoomCat = (r) => {
    return (
      <div style={{ paddingTop: 20 }}>
        <Text bold>{r.title}</Text>
        {isLoading && <CenterLoader />}
        {!size(r.items) && !isLoading && r.key == '0' && <div>No rooms enabled; drag and drop rooms here to get started</div>}
      </div>
    );
  };

  useEffect(() => {
    if (scdata.loading || roomData.loading) {
      return;
    }
    const card = scdata.data;
    const base = get(card, 'checklists', []);
    if (!size(base)) {
      setChecklists([]);
      setRooms(EMPTY_ROOM_GROUPS());
      return;
    }
    const { checklists: merged, rooms: mergedRooms } = mergeScorecardRoomAssignments(card, roomData.data);
    setChecklists(merged);
    setRooms(mergedRooms);
  }, [scorecard.id, scdata.loading, roomData.loading, scdata.data, roomData.data]);

  useEffect(() => {
    if (!preview.open) {
      setPreviewShowOnlyChecked(false);
    }
  }, [preview.open]);

  const checkpointCountByRoomId = useMemo(() => {
    const counts = new Map();
    for (const cl of checklists) {
      for (const cp of cl.checkpoints || []) {
        for (const r of get(cp, 'rooms', [])) {
          const id = String(r.id);
          counts.set(id, (counts.get(id) || 0) + 1);
        }
      }
    }
    return counts;
  }, [checklists]);

  const enabledRoomIds = useMemo(
    () => new Set((get(rooms, '0.items', []) || []).map((i) => String(i.id))),
    [rooms]
  );

  const enabledRooms = useMemo(() => {
    const data = roomData.data || [];
    return data.filter((r) => enabledRoomIds.has(String(r.id)));
  }, [roomData.data, enabledRoomIds]);

  const onChange = useCallback(() => {
    notifyParentChange && notifyParentChange();
  }, [notifyParentChange]);

  /** Immutable updates so memoized rows/checklists can skip re-render when their data is unchanged. */
  const updateChecks = useCallback(
    (checklist, sel, cp, areEqual) => {
      setChecklists((clss) =>
        clss.map((cls) => {
          if (String(cls.id) !== String(checklist.id)) {
            return cls;
          }
          if (!cp) {
            return {
              ...cls,
              rooms: sel,
              checkpoints: (cls.checkpoints || []).map((c) => ({ ...c, rooms: sel })),
            };
          }
          return {
            ...cls,
            checkpoints: (cls.checkpoints || []).map((c) => (String(c.id) === String(cp.id) ? { ...c, rooms: sel } : c)),
          };
        })
      );

      if (!areEqual) {
        onChange();
      }
    },
    [onChange]
  );

  const onCopy = useCallback((rms) => {
    setState((s) => ({ ...s, copiedRooms: rms }));
    setAlerts(settingsCopiedSnackAlert());
  }, []);

  const onCopyLocationAccess = (room) => {
    setState((s) => ({ ...s, copiedLocationAccess: { id: room.id, name: room.name } }));
    setAlerts(settingsCopiedSnackAlert());
  };

  const onPasteLocationAccess = (targetRoom) => {
    const source = state.copiedLocationAccess;
    if (!source || String(source.id) === String(targetRoom.id)) {
      return;
    }
    const sourceRef = { id: source.id, name: source.name };
    const targetRef = { id: targetRoom.id, name: targetRoom.name };
    setChecklists((prev) => applyRoomLocationAccessFromSourceToTarget(prev, sourceRef, targetRef));
    onChange();
  };

  const cancel = () => {
    props.onCancel && props.onCancel();
  };

  const getCurrentRoomCheckpoints = () => {
    const roomToCpIds = new Map();
    for (const cl of checklists) {
      for (const cp of cl.checkpoints || []) {
        for (const r of get(cp, 'rooms', [])) {
          const id = String(r.id);
          if (!roomToCpIds.has(id)) {
            roomToCpIds.set(id, []);
          }
          roomToCpIds.get(id).push(cp.id);
        }
      }
    }
    const rcps = [];
    for (const group of rooms) {
      const { items, key } = group;
      for (const rm of items || []) {
        const rid = String(rm.id);
        rcps.push({
          id: rm.id,
          cps: roomToCpIds.get(rid) || [],
          act: key == '0',
        });
      }
    }
    return rcps;
  };

  const previewClick = (realm) => {
    setPreview((p) => ({
      ...p,
      realm,
      open: true,
      subTitle: `${realm.name} — Edit criteria access`,
    }));
  };

  const renderRoom = (r, group) => {
    const count = checkpointCountByRoomId.get(String(r.id)) || 0;
    const isDisabled = group.key != '0';
    return (
      <AssignmentLocationRow
        locationId={r.id}
        name={r.name}
        countLabel={`${count} Checkpoints`}
        copiedLocationAccess={state.copiedLocationAccess}
        rowDimmed={isDisabled}
        previewDisabled={isDisabled}
        copyDisabled={isDisabled}
        onPreview={() => previewClick(r)}
        onCopyLocationAccess={() => onCopyLocationAccess(r)}
        onPasteLocationAccess={() => onPasteLocationAccess(r)}
        titlePreview="Edit which criteria this room can access"
        titleCopy="Copy criteria access for this room"
        titlePaste="Paste copied room access"
      />
    );
  };

  const submit = async () => {
    try {
      setState((s) => ({ ...s, loading: true }));
      const room_checkpoints = getCurrentRoomCheckpoints();

      const config = get(scdata, 'data.config', {});

      const data = {
        data: {
          id: scorecard.id,
          type: 'scorecards',
          attributes: {
            config: {
              ...convertToPostStructure(config),
              room_checkpoints,
            },
          },
        },
      };

      await sendData(
        {
          url: `/scorecards/${scorecard.id}`,
          method: 'patch',
          data,
        },
        { simulate: false }
      );

      props.onClose && props.onClose(true);
    } catch (err) {
      console.warn(err);
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const handleSubmit = () => {
    const room_checkpoints = getCurrentRoomCheckpoints();
    props.onSubmit?.(room_checkpoints, scdata) ?? submit();
  };

  return (
    <RoomAssignmentsContainer style={{ paddingBottom: 72 }}>
      <AssignmentFormTabs
        title="Room-Assignments"
        subtitleAdd={get(props, 'subtitleAdd', '')}
        subtitle="Room assignments are only applicable to Lot-level Assessments. Not all rooms need to be used."
        value={mainTab}
        onChange={setMainTab}
      />

      {mainTab === ASSIGNMENT_FORM_TAB.location && (
        <AssignmentFormTabPanel
          title="Room Order"
          description="Drag rooms between enabled and disabled, set their order, and use row actions to edit criteria access or copy access between rooms."
        >
          <SortableList
            items={(() => {
              let roomStripeIndex = 0;
              return rooms.map((r) => ({
                ...r,
                content: () => renderRoomCat(r),
                items: r.items.map((room) => ({
                  ...room,
                  key: `${room.id}`,
                  stripeBackground: roomStripeIndex++ % 2 === 1 ? theme.palette.action.disabledBackground : undefined,
                  content: () => renderRoom(room, r),
                })),
              }));
            })()}
            verticalOnly
            onSort={(r) => {
              setRooms(r);
              onChange();
            }}
          />
        </AssignmentFormTabPanel>
      )}

      {mainTab === ASSIGNMENT_FORM_TAB.criteria && (
        <AssignmentFormTabPanel title="Criteria Assignment">
          {criteriaLoading ? (
            <CenterLoader />
          ) : (
            !preview.open &&
            checklists.map((c) => (
              <RoomAssignmentChecklist
                supersets={supersets.data}
                checklist={c}
                key={c.id}
                updateChecks={updateChecks}
                onCopy={onCopy}
                isMobileXS={isMobileXS}
                rooms={enabledRooms}
                {...state}
              />
            ))
          )}
        </AssignmentFormTabPanel>
      )}

      <AssignmentFormFooter onCancel={cancel} onSave={handleSubmit} saving={state.loading} />

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />
      <AssignmentPreviewDialogFrame
        open={preview.open}
        onClose={preview.onClose}
        title={preview.subTitle}
        filter={
          preview.realm
            ? {
                active: previewShowOnlyChecked,
                onToggle: () => setPreviewShowOnlyChecked((v) => !v),
                titleShowAll: 'Show all criteria',
                titleShowOnly: 'Show only assigned criteria',
              }
            : undefined
        }
      >
        {checklists.map((c) => (
          <RoomAssignmentChecklist
            supersets={supersets.data}
            checklist={c}
            key={c.id}
            updateChecks={updateChecks}
            onCopy={onCopy}
            isMobileXS={isMobileXS}
            rooms={enabledRooms}
            previewRealm={preview.realm}
            previewShowOnlyChecked={previewShowOnlyChecked}
            {...state}
          />
        ))}
        {previewShowOnlyChecked &&
          preview.realm &&
          !checklists.some((cl) => (cl.checkpoints || []).some((cp) => isCheckpointTaggedForRoom(cp, preview.realm))) && (
            <Text type="label_large" style={{ marginTop: 16 }}>
              No criteria are assigned to this room.
            </Text>
          )}
      </AssignmentPreviewDialogFrame>
    </RoomAssignmentsContainer>
  );
};

export const RoomAssignmentsModal = (props) => (
  <AssignmentsModalFrame modalProps={props.modalProps}>{(handlers) => <RoomAssignments {...props} {...handlers} />}</AssignmentsModalFrame>
);
